<template>
  <div class="export-csv-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>订单数据CSV导出工具</span>
      </div>
      
      <div class="content">
        <el-alert
          title="说明"
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 20px;">
          <div>
            <p>本工具将生成并导出订单CSV数据文件，支持自定义配置：</p>
            <ul>
              <li>可设置年份、月份</li>
              <li>可设置各类订单数量</li>
              <li>自动从接口获取课程类目，随机拼接成标题</li>
              <li>日期格式：yyyy-MM-dd HH:mm:ss</li>
              <li>下单时间范围：每天06:00-24:00</li>
            </ul>
          </div>
        </el-alert>

        <!-- 配置表单 -->
        <el-form :model="form" label-width="140px" class="config-form">
          <el-divider content-position="left">时间配置</el-divider>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="年份">
                <el-input-number 
                  v-model="form.year" 
                  :min="2020" 
                  :max="2030"
                  style="width: 100%;">
                </el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="月份">
                <el-select v-model="form.month" placeholder="请选择月份" style="width: 100%;">
                  <el-option
                    v-for="item in 12"
                    :key="item"
                    :label="item + '月'"
                    :value="item">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider content-position="left">订单数量配置</el-divider>
          
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="教练订单数量">
                <el-input-number 
                  v-model="form.coachOrderCount" 
                  :min="0" 
                  :max="10000"
                  style="width: 100%;">
                </el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="门店订单数量">
                <el-input-number 
                  v-model="form.storeOrderCount" 
                  :min="0" 
                  :max="10000"
                  style="width: 100%;">
                </el-input-number>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="会员卡订单数量">
                <el-input-number 
                  v-model="form.memberOrderCount" 
                  :min="0" 
                  :max="10000"
                  style="width: 100%;">
                </el-input-number>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="会员卡价格">
                <el-input-number 
                  v-model="form.memberPrice" 
                  :min="0" 
                  :precision="2"
                  style="width: 100%;">
                </el-input-number>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider content-position="left">价格分布配置（课程订单）</el-divider>
          
          <div class="price-config">
            <div v-for="(item, index) in form.priceDistribution" :key="index" class="price-item">
              <el-row :gutter="10">
                <el-col :span="8">
                  <el-input-number 
                    v-model="item.amount" 
                    :min="0" 
                    :precision="2"
                    placeholder="价格"
                    style="width: 100%;">
                  </el-input-number>
                </el-col>
                <el-col :span="8">
                  <el-input-number 
                    v-model="item.count" 
                    :min="0" 
                    placeholder="数量"
                    style="width: 100%;">
                  </el-input-number>
                </el-col>
                <el-col :span="8">
                  <el-button 
                    type="danger" 
                    icon="el-icon-delete" 
                    size="small"
                    @click="removePriceItem(index)"
                    :disabled="form.priceDistribution.length <= 1">
                    删除
                  </el-button>
                </el-col>
              </el-row>
            </div>
            <el-button 
              type="primary" 
              icon="el-icon-plus" 
              size="small"
              @click="addPriceItem"
              style="margin-top: 10px;">
              添加价格档位
            </el-button>
          </div>
        </el-form>

        <div class="action-area">
          <el-button 
            type="primary" 
            size="large" 
            icon="el-icon-download"
            @click="handleExport"
            :loading="exporting">
            {{ exporting ? '生成中...' : '导出CSV文件' }}
          </el-button>
          <el-button 
            size="large" 
            icon="el-icon-refresh"
            @click="resetForm">
            重置默认值
          </el-button>
        </div>

        <div v-if="exportSuccess" class="success-message">
          <el-alert
            title="导出成功！"
            type="success"
            :closable="false"
            show-icon>
          </el-alert>
        </div>

        <!-- 统计信息 -->
        <div v-if="lastExportStats" class="stats-info">
          <el-divider content-position="left">上次导出统计</el-divider>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="总订单数">{{ lastExportStats.total }}</el-descriptions-item>
            <el-descriptions-item label="教练订单">{{ lastExportStats.coachOrders }}</el-descriptions-item>
            <el-descriptions-item label="门店订单">{{ lastExportStats.storeOrders }}</el-descriptions-item>
            <el-descriptions-item label="会员卡订单">{{ lastExportStats.memberOrders }}</el-descriptions-item>
            <el-descriptions-item label="年份">{{ lastExportStats.year }}</el-descriptions-item>
            <el-descriptions-item label="月份">{{ lastExportStats.month }}月</el-descriptions-item>
          </el-descriptions>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { exportOrderCsv } from '@/utils/generateOrderCsv';

export default {
  name: 'ExportCsv',
  data() {
    return {
      exporting: false,
      exportSuccess: false,
      lastExportStats: null,
      form: {
        year: 2022,
        month: 7,
        coachOrderCount: 1200,
        storeOrderCount: 300,
        memberOrderCount: 38,
        memberPrice: 698,
        priceDistribution: [
          { amount: 9.9, count: 938 },
          { amount: 99, count: 351 },
          { amount: 199, count: 141 },
          { amount: 1680, count: 47 },
          { amount: 2480, count: 23 }
        ]
      }
    };
  },
  methods: {
    async handleExport() {
      this.exporting = true;
      this.exportSuccess = false;
      
      try {
        // 构建导出选项
        const options = {
          year: this.form.year,
          month: this.form.month,
          coachOrderCount: this.form.coachOrderCount,
          storeOrderCount: this.form.storeOrderCount,
          memberOrderCount: this.form.memberOrderCount,
          memberPrice: this.form.memberPrice,
          priceDistribution: this.form.priceDistribution
        };
        
        // 生成文件名
        const filename = `order_data_${this.form.year}_${this.form.month.toString().padStart(2, '0')}.csv`;
        
        // 导出CSV
        await exportOrderCsv(options, filename);
        
        // 记录统计信息
        this.lastExportStats = {
          total: this.form.coachOrderCount + this.form.storeOrderCount + this.form.memberOrderCount,
          coachOrders: this.form.coachOrderCount,
          storeOrders: this.form.storeOrderCount,
          memberOrders: this.form.memberOrderCount,
          year: this.form.year,
          month: this.form.month
        };
        
        this.exportSuccess = true;
        this.$message.success('CSV文件已成功导出！');
      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败，请重试');
      } finally {
        this.exporting = false;
      }
    },
    
    resetForm() {
      this.form = {
        year: 2022,
        month: 7,
        coachOrderCount: 1200,
        storeOrderCount: 300,
        memberOrderCount: 38,
        memberPrice: 698,
        priceDistribution: [
          { amount: 9.9, count: 938 },
          { amount: 99, count: 351 },
          { amount: 199, count: 141 },
          { amount: 1680, count: 47 },
          { amount: 2480, count: 23 }
        ]
      };
      this.$message.info('已重置为默认值');
    },
    
    addPriceItem() {
      this.form.priceDistribution.push({
        amount: 0,
        count: 0
      });
    },
    
    removePriceItem(index) {
      this.form.priceDistribution.splice(index, 1);
    }
  }
};
</script>

<style scoped lang="less">
.export-csv-container {
  padding: 20px;
  
  .box-card {
    max-width: 900px;
    margin: 0 auto;
  }
  
  .content {
    ul {
      margin: 10px 0;
      padding-left: 20px;
      
      li {
        margin: 5px 0;
        line-height: 1.6;
      }
    }
  }
  
  .config-form {
    margin-bottom: 30px;
    
    .price-config {
      .price-item {
        margin-bottom: 10px;
        padding: 10px;
        background-color: #f5f7fa;
        border-radius: 4px;
      }
    }
  }
  
  .action-area {
    text-align: center;
    margin: 30px 0;
    
    .el-button {
      padding: 15px 40px;
      font-size: 16px;
      margin: 0 10px;
    }
  }
  
  .success-message {
    margin-top: 20px;
  }
  
  .stats-info {
    margin-top: 30px;
  }
}
</style>